"use client";

import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Input,
  Text,
  useColorMode,
  Link,
  Image,
  Heading,
  Center,
  Circle,
  FormControl,
  FormErrorMessage,
  Stack,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import GiveawayBanner from "../components/GiveawayBanner/GiveawayBanner";
import cci from "../assets/image/main-page/brands/CCI_C.png";
import hevishoot from "../assets/image/main-page/brands/HEVI SHOT_Colored.png";
import { getCurrentColor } from "../constants";
import { Helmet } from "react-helmet";

function GleamWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://widget.gleamjs.io/e.js";
    script.async = true;

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <Box ref={containerRef} w="full" maxW="4xl" mx="auto" overflow="visible">
      <a
        className="e-widget no-button"
        href="https://gleam.io/fuYv1/free-ammo-for-life-giveaway"
        rel="nofollow"
      >
        Free Ammo For Life Giveaway!
      </a>
    </Box>
  );
}

const brands = [
  {
    white: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Barnes_C.png",
    black: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Barnes_W.png",
  },
  {
    white: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Armscor_B_A.png",
    black: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Armscor_W.png",
  },
  {
    white: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Fiocchi_C.png",
    black: "https://d337jieq2masmw.cloudfront.net/2024-08-12/Fiocchi_W.png",
  },
  {
    white: "https://d337jieq2masmw.cloudfront.net/2024-08-12/PMC_C.png",
    black: "https://d337jieq2masmw.cloudfront.net/2024-08-12/PMC_W.png",
  },

  {
    white: cci,
    black: cci,
  },
  {
    white: hevishoot,
    black: hevishoot,
  },
];

export default function Giveaway() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const { colorMode } = useColorMode();

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email || !email.includes("@") || !email.includes(".")) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    setSubmitted(true);
  };

  const priceDetailsData = [
    {
      title: "Lifetime Supply",
      description: "Receive one case of ammunition every year for 40 years.",
    },
    {
      title: "You Choose",
      description: "Choose between 9mm or 5.56x45mm as your prize of choice.",
    },
    {
      title: "Premium Quality",
      description: "Top-tier ammunition from leading manufacturers.",
    },
    {
      title: "No Purchase Necessary",
      description:
        "Enter with just your email. Open to all eligible participants 21+.",
    },
  ];

  const renderPriceDetails = (priceDetailsData) => {
    return (
      <Card variant="outline">
        <CardBody
          textAlign="center"
          bg={getCurrentColor(colorMode, "blueC.1200", "")}
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
            color={getCurrentColor(colorMode, "gray.700", "whiteC.100")}
            mb={2}
          >
            {priceDetailsData.title}
          </Text>
          <Text
            fontSize="lg"
            color={getCurrentColor(colorMode, "gray.700", "whiteC.100")}
          >
            {priceDetailsData.description}
          </Text>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box minHeight="100vh">
      <Helmet>
        <title>Giveaway from Bullet Scout</title>
        <meta
          name="description"
          content="Bullet Scout, your premier destination for finding the right ammunition for your firearm needs. With our comprehensive search engine, you can explore a wide array of ammunition options, ensuring you always hit your mark."
        />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-JH75YYT0PY"
        />
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-JH75YYT0PY');
          `}
        </script>
      </Helmet>
      <Box as="main" flexGrow={1}>
        <Box py={{ base: 8, md: 16 }} px={4}>
          <Box maxW="7xl" mx="auto">
            <Box
              bgGradient="linear(to-r, #0a0f2a, #1a2563, #0a0f2a)"
              rounded="xl"
              shadow="2xl"
              overflow="hidden"
            >
              <GiveawayBanner />
            </Box>
          </Box>
        </Box>

        <Box py={{ base: 8, md: 12 }} px={4}>
          <Box maxW="6xl" mx="auto">
            <Text
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              textAlign="center"
              color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
              mb={6}
            >
              Prize Details
            </Text>
            <Box
              display="grid"
              gridTemplateColumns={{
                base: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, 1fr)",
              }}
              gap={6}
            >
              {priceDetailsData.map((priceDetail, index) =>
                renderPriceDetails(priceDetail),
              )}
            </Box>
          </Box>
        </Box>

        <Box py={{ base: 8, md: 12 }} px={4}>
          <Box maxW="4xl" mx="auto">
            <Card
              boxShadow="md"
              border="none"
              bg={getCurrentColor(colorMode, "", "blueC.1000")}
            >
              <CardBody p={{ base: 5, md: 8 }}>
                {!submitted ? (
                  <>
                    <Heading
                      as="h2"
                      size={{ base: "2xl", md: "3xl" }}
                      textAlign="center"
                      color={getCurrentColor(
                        colorMode,
                        "#0a0f2a",
                        "whiteC.100",
                      )}
                      mb={{ base: 4, md: 6 }}
                      fontWeight="bold"
                    >
                      Enter Your Email to Win
                    </Heading>
                    <Text
                      textAlign="center"
                      fontSize={{ base: "md", md: "lg" }}
                      color={getCurrentColor(
                        colorMode,
                        "gray.700",
                        "whiteC.100",
                      )}
                      mb={{ base: 6, md: 8 }}
                      maxW={"80%"}
                      mx={"auto"}
                    >
                      Simply enter your email address below for a chance to win
                      a lifetime supply of ammunition. One lucky winner will
                      receive one free case of ammunition per year for the next
                      40 years.
                    </Text>
                    <form onSubmit={handleSubmit}>
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        spacing={{ base: 3, md: 4 }}
                        maxW="md"
                        mx="auto"
                      >
                        <FormControl isInvalid={!!emailError}>
                          <Input
                            type="email"
                            placeholder="Enter your email address"
                            size="lg"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                          {emailError && (
                            <FormErrorMessage>{emailError}</FormErrorMessage>
                          )}
                        </FormControl>
                        <Button
                          type="submit"
                          bg="#0a0f2a"
                          _hover={{ bg: "blue.800" }}
                          color="white"
                          size="lg"
                          flexShrink={0}
                        >
                          Enter Contest
                        </Button>
                      </Stack>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        mt={{ base: 3, md: 4 }}
                        maxW="md"
                        mx="auto"
                      >
                        By entering, you agree to our{" "}
                        <Link
                          href="/terms"
                          textDecoration="underline"
                          color="blue.600"
                          _hover={{ color: "blue.800" }}
                        >
                          Terms & Conditions
                        </Link>{" "}
                        and
                        <Link
                          href="/privacy"
                          textDecoration="underline"
                          color="blue.600"
                          _hover={{ color: "blue.800" }}
                        >
                          {" "}
                          Privacy Policy
                        </Link>
                        . Must be 21+ to enter. No purchase necessary.
                      </Text>
                    </form>
                  </>
                ) : (
                  <>
                    <Heading
                      as="h2"
                      size={{ base: "2xl", md: "3xl" }}
                      textAlign="center"
                      color="#0a0f2a"
                      fontWeight="bold"
                      mb={6}
                    >
                      Complete Your Entry
                    </Heading>
                    <Center mb={6}>
                      <Circle
                        size={{ base: "56px", md: "64px" }}
                        bg="green.100"
                      >
                        <CheckIcon
                          boxSize={{ base: 6, md: 8 }}
                          color="green.600"
                        />
                      </Circle>
                    </Center>
                    <Text
                      textAlign="center"
                      fontSize={{ base: "md", md: "lg" }}
                      color="gray.700"
                      mb={6}
                      maxW={"90%"}
                      mx={"auto"}
                    >
                      Thanks for entering with <strong>{email}</strong>!
                      Complete the actions below to increase your chances of
                      winning.
                    </Text>

                    <Box w="full" display="flex" justifyContent="center">
                      <GleamWidget />
                    </Box>
                  </>
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>

        <Box py={{ base: 8, md: 12 }} px={4}>
          <Box maxW="7xl" mx="auto">
            <Text
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              textAlign="center"
              color={getCurrentColor(colorMode, "gray.700", "whiteC.100")}
              mb={6}
            >
              Sponsors and Brands
            </Text>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              justifyItems="center"
            >
              {brands.map((brand, i) => (
                <Flex
                  justifyContent={"center"}
                  key={i}
                  alignItems={"center"}
                  w={{ base: "150px", "1xs": "185px", lg: "198px" }}
                  h={{ base: "76px", lg: "82px" }}
                  borderRadius={"5px"}
                  border={"1px solid "}
                  borderColor={getCurrentColor(
                    colorMode,
                    "grayC.900",
                    "grayC.2400",
                  )}
                  boxShadow={
                    "0px 4px 14px 0px rgba(183, 183, 183, 0.02), 0px 20px 40px 0px rgba(183, 183, 183, 0.06)"
                  }
                  bg={getCurrentColor(colorMode, "whiteC.100", "transparent")}
                  padding="15px"
                >
                  <Image
                    key={i}
                    src={colorMode === "dark" ? brand.black : brand.white}
                    alt={brand.white}
                    width={100}
                    height={100}
                    objectFit="contain"
                  />
                </Flex>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
